import type { BrandingColores, BrandingColoresResuelto } from "./branding";

/**
 * Helpers de color del branding por institución (ADR 045 / 046 / 047).
 *
 * El sistema almacena los colores como tripletes `"R G B"` (formato de las CSS
 * variables de `globals.css`). El usuario, en cambio, piensa en HEX y elige con
 * un color picker. Estos helpers:
 *
 *  1. convierten HEX ↔ "R G B";
 *  2. **derivan la paleta completa** (rampa de marca + rampas semánticas
 *     `success` / `info`) a partir de unos pocos colores semilla, de modo que el
 *     usuario sólo elija identidad y el resto sea coherente y legible;
 *  3. **garantizan contraste WCAG** en los tokens que se usan como texto: nadie
 *     puede dejar un acento ilegible (el error clásico del amarillo sobre blanco).
 *
 * La derivación es determinista y pura (sin estado), para poder testearla y para
 * que el mismo seed produzca siempre la misma paleta en UI y en documentos.
 */

const HEX_RE = /^#?([0-9a-fA-F]{6})$/;

/** Blanco y superficie oscura de referencia para los chequeos de contraste. */
const BLANCO = "255 255 255";
/** Fondo del modo oscuro (zinc-900) — debe coincidir con `--background` en `.dark`. */
const SUPERFICIE_OSCURA = "24 24 27";

/** Semillas por defecto de los acentos semánticos (verde éxito / azul info). */
const SEMILLA_EXITO_DEFAULT = "22 163 74"; // green-600
const SEMILLA_INFO_DEFAULT = "37 99 235"; // blue-600

/** Contraste mínimo para texto de UI / badges (WCAG AA texto grande/componente). */
const CONTRASTE_MIN_UI = 3;

/** `#2EBAA1` (o `2EBAA1`) → `"46 186 161"`. Devuelve null si el hex es inválido. */
export function hexARgbTriplet(hex: string): string | null {
  const m = HEX_RE.exec(hex.trim());
  const hexDigits = m?.[1];
  if (!hexDigits) return null;
  const n = parseInt(hexDigits, 16);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  return `${r} ${g} ${b}`;
}

/** `"46 186 161"` → `#2ebaa1`. Devuelve null si el triplete es inválido. */
export function rgbTripletAHex(triplet: string): string | null {
  const partes = triplet.trim().split(/\s+/);
  if (partes.length !== 3) return null;
  const nums = partes.map((p) => Number(p));
  if (nums.some((n) => !Number.isInteger(n) || n < 0 || n > 255)) return null;
  return "#" + nums.map((n) => n.toString(16).padStart(2, "0")).join("");
}

/** Parsea `"R G B"` a `[r, g, b]` numéricos, o null si es inválido. */
function parseTriplet(triplet: string): [number, number, number] | null {
  const partes = triplet.trim().split(/\s+/);
  if (partes.length !== 3) return null;
  const [r, g, b] = partes.map((p) => Number(p));
  if (r === undefined || g === undefined || b === undefined) return null;
  if ([r, g, b].some((n) => !Number.isFinite(n) || n < 0 || n > 255)) return null;
  return [r, g, b];
}

function tripletStr(r: number, g: number, b: number): string {
  return `${r} ${g} ${b}`;
}

/** Escala un componente hacia negro (factor < 1) o hacia blanco (mezcla con 255). */
function ajustarComponente(componente: number, factor: number, haciaBlanco: boolean): number {
  const v = haciaBlanco ? componente + (255 - componente) * factor : componente * factor;
  return Math.max(0, Math.min(255, Math.round(v)));
}

/** Aplica `ajustarComponente` a un triplete completo. */
function ajustarTriplet(triplet: string, factor: number, haciaBlanco: boolean): string {
  const rgb = parseTriplet(triplet);
  if (!rgb) return triplet;
  const [r, g, b] = rgb;
  return tripletStr(
    ajustarComponente(r, factor, haciaBlanco),
    ajustarComponente(g, factor, haciaBlanco),
    ajustarComponente(b, factor, haciaBlanco),
  );
}

// --- Contraste WCAG ---------------------------------------------------------

/** Linealiza un canal sRGB [0,255] al espacio lineal (fórmula WCAG 2.x). */
function canalLineal(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/**
 * Luminancia relativa WCAG de un color `"R G B"` (0 = negro, 1 = blanco).
 * Devuelve null si el triplete es inválido.
 */
export function luminanciaRelativa(triplet: string): number | null {
  const rgb = parseTriplet(triplet);
  if (!rgb) return null;
  const [r, g, b] = rgb;
  return 0.2126 * canalLineal(r) + 0.7152 * canalLineal(g) + 0.0722 * canalLineal(b);
}

/**
 * Relación de contraste WCAG entre dos colores (1 = idéntico, 21 = negro/blanco).
 * AA pide ≥ 4.5 para texto normal y ≥ 3 para texto grande / componentes de UI.
 * Devuelve null si algún triplete es inválido.
 */
export function relacionContraste(t1: string, t2: string): number | null {
  const l1 = luminanciaRelativa(t1);
  const l2 = luminanciaRelativa(t2);
  if (l1 === null || l2 === null) return null;
  const [hi, lo] = l1 >= l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Ajusta `color` (oscureciéndolo o aclarándolo según el fondo) hasta alcanzar al
 * menos `ratio` de contraste contra `fondo`. Es la garantía anti-ilegibilidad:
 * si un acento elegido por el usuario no contrasta, lo empuja hasta que sí.
 * Determinista y acotado (máx. 24 pasos del 8%).
 */
export function forzarContraste(color: string, fondo: string, ratio: number): string {
  const lf = luminanciaRelativa(fondo);
  if (lf === null) return color;
  const haciaBlanco = lf <= 0.5; // fondo oscuro ⇒ aclarar; fondo claro ⇒ oscurecer
  let actual = color;
  for (let i = 0; i < 24; i++) {
    const c = relacionContraste(actual, fondo);
    if (c !== null && c >= ratio) return actual;
    actual = ajustarTriplet(actual, 0.08, haciaBlanco);
  }
  return actual;
}

// --- Conversión HSL (para derivar neutros tintados) -------------------------

/**
 * `[r,g,b]` (0-255) → `[h, s, l]` con `h` en [0,360), `s`/`l` en [0,1].
 * Algoritmo estándar; útil para separar el TONO (que aporta el tenant) de la
 * luminosidad (que fija el sistema) al derivar superficies neutras.
 */
function rgbAHsl(r: number, g: number, b: number): [number, number, number] {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return [0, 0, l]; // gris: tono indefinido ⇒ 0
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;
  return [h * 360, s, l];
}

/** `[h,s,l]` (`h` en [0,360), `s`/`l` en [0,1]) → triplete `"R G B"`. */
function hslATriplet(h: number, s: number, l: number): string {
  const hn = ((((h % 360) + 360) % 360) / 360) % 1;
  if (s === 0) {
    const v = Math.round(l * 255);
    return tripletStr(v, v, v);
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const canal = (t: number): number => {
    let tn = t;
    if (tn < 0) tn += 1;
    if (tn > 1) tn -= 1;
    if (tn < 1 / 6) return p + (q - p) * 6 * tn;
    if (tn < 1 / 2) return q;
    if (tn < 2 / 3) return p + (q - p) * (2 / 3 - tn) * 6;
    return p;
  };
  return tripletStr(
    Math.round(canal(hn + 1 / 3) * 255),
    Math.round(canal(hn) * 255),
    Math.round(canal(hn - 1 / 3) * 255),
  );
}

/**
 * Superficies neutras tintadas, por tema (cada una un triplete `"R G B"` listo
 * para CSS variables). El contraste crece `bg` → `card` → `muted` → `border`.
 */
export interface NeutralesResueltos {
  bgClaro: string;
  cardClaro: string;
  mutedClaro: string;
  borderClaro: string;
  bgOscuro: string;
  cardOscuro: string;
  mutedOscuro: string;
  borderOscuro: string;
}

/**
 * Cada superficie se define por su LUMINOSIDAD fija (la fija el sistema) y un
 * TOPE de saturación sutil. El tono lo aporta el tenant. Como la luminosidad
 * está clavada (muy alta en claro, muy baja en oscuro) y el texto
 * (`--foreground`) también es fijo por tema, el contraste queda garantizado sin
 * importar el color elegido — esa es la restricción que pidió el SaaS.
 */
const NIVELES_NEUTRALES = {
  bgClaro: { l: 0.975, s: 0.3 },
  cardClaro: { l: 0.995, s: 0.22 },
  mutedClaro: { l: 0.955, s: 0.34 },
  borderClaro: { l: 0.9, s: 0.34 },
  bgOscuro: { l: 0.065, s: 0.5 },
  cardOscuro: { l: 0.115, s: 0.46 },
  mutedOscuro: { l: 0.15, s: 0.42 },
  borderOscuro: { l: 0.2, s: 0.38 },
} as const;

/**
 * Deriva las superficies neutras (fondo/card/muted/borde, en claro y oscuro) a
 * partir de un color de fondo semilla (`"R G B"`). Toma SÓLO el tono: la
 * luminosidad de cada superficie es fija por tema y la saturación se acota a un
 * tinte sutil (`min(saturación elegida, tope)`), de modo que un color vívido no
 * sature el fondo ni reduzca el contraste del contenido. Un gris (saturación 0)
 * produce superficies neutras puras. Devuelve null si el triplete es inválido.
 */
export function derivarNeutrales(fondoTriplet: string): NeutralesResueltos | null {
  const rgb = parseTriplet(fondoTriplet);
  if (!rgb) return null;
  const [h, s] = rgbAHsl(rgb[0], rgb[1], rgb[2]);
  const nivel = (cfg: { l: number; s: number }): string =>
    hslATriplet(h, Math.min(s, cfg.s), cfg.l);
  return {
    bgClaro: nivel(NIVELES_NEUTRALES.bgClaro),
    cardClaro: nivel(NIVELES_NEUTRALES.cardClaro),
    mutedClaro: nivel(NIVELES_NEUTRALES.mutedClaro),
    borderClaro: nivel(NIVELES_NEUTRALES.borderClaro),
    bgOscuro: nivel(NIVELES_NEUTRALES.bgOscuro),
    cardOscuro: nivel(NIVELES_NEUTRALES.cardOscuro),
    mutedOscuro: nivel(NIVELES_NEUTRALES.mutedOscuro),
    borderOscuro: nivel(NIVELES_NEUTRALES.borderOscuro),
  };
}

// --- Derivación de paletas --------------------------------------------------

/** Rampa de marca 50..900 a partir del color base (brand500). */
function rampaMarca(base: string): {
  brand50: string;
  brand100: string;
  brand200: string;
  brand300: string;
  brand400: string;
  brand500: string;
  brand600: string;
  brand700: string;
  brand800: string;
  brand900: string;
} {
  return {
    brand50: ajustarTriplet(base, 0.92, true),
    brand100: ajustarTriplet(base, 0.85, true),
    brand200: ajustarTriplet(base, 0.72, true),
    brand300: ajustarTriplet(base, 0.55, true),
    brand400: ajustarTriplet(base, 0.3, true),
    brand500: base,
    brand600: ajustarTriplet(base, 0.82, false),
    brand700: ajustarTriplet(base, 0.62, false),
    brand800: ajustarTriplet(base, 0.45, false),
    brand900: ajustarTriplet(base, 0.3, false),
  };
}

/**
 * Rampa de un acento semántico (success / info) a partir de su semilla. Genera:
 *  - `s50`: tinte muy claro para fondos.
 *  - `s400`: paso CLARO legible sobre superficie OSCURA (texto del badge en dark).
 *  - `s500`: la semilla tal cual (relleno a opacidad / decoración).
 *  - `s600`: paso OSCURO legible sobre BLANCO (texto del badge en claro).
 *  - `s700`: el más oscuro.
 *
 * `s400` y `s600` están **garantizados ≥ 3:1** contra su fondo: el badge es
 * legible en ambos temas pase lo que pase con la semilla elegida.
 */
function rampaSemantica(semilla: string): {
  s50: string;
  s400: string;
  s500: string;
  s600: string;
  s700: string;
} {
  return {
    s50: ajustarTriplet(semilla, 0.9, true),
    s400: forzarContraste(ajustarTriplet(semilla, 0.35, true), SUPERFICIE_OSCURA, CONTRASTE_MIN_UI),
    s500: semilla,
    s600: forzarContraste(ajustarTriplet(semilla, 0.85, false), BLANCO, CONTRASTE_MIN_UI),
    s700: ajustarTriplet(semilla, 0.55, false),
  };
}

/** Ensambla la paleta resuelta completa a partir de los tripletes semilla. */
function construirPaleta(seeds: {
  primario: string;
  acento: string;
  exito: string;
  info: string;
}): BrandingColoresResuelto {
  const marca = rampaMarca(seeds.primario);
  const exito = rampaSemantica(seeds.exito);
  const info = rampaSemantica(seeds.info);
  return {
    ...marca,
    gold500: seeds.acento,
    success50: exito.s50,
    success400: exito.s400,
    success500: exito.s500,
    success600: exito.s600,
    success700: exito.s700,
    info50: info.s50,
    info400: info.s400,
    info500: info.s500,
    info600: info.s600,
    info700: info.s700,
  };
}

/**
 * Deriva la paleta `BrandingColoresResuelto` completa a partir de los colores
 * semilla en HEX. `primarioHex` genera la rampa de marca; `acentoHex` va a
 * `gold500`; `exitoHex` / `infoHex` (opcionales) afinan los acentos semánticos
 * (badges de estado) — si se omiten, se usan verde/azul por defecto.
 *
 * Garantiza tokens de texto legibles (rampa de marca con `brand700` oscuro, y
 * `success`/`info` con contraste AA en claro y oscuro). `fondoHex` (opcional)
 * tinta las superficies neutras; ausente ⇒ neutros navy por defecto. Devuelve
 * null si algún hex provisto es inválido.
 */
export function derivarPaleta(
  primarioHex: string,
  acentoHex: string,
  exitoHex?: string,
  infoHex?: string,
  fondoHex?: string,
): BrandingColoresResuelto | null {
  const primario = hexARgbTriplet(primarioHex);
  const acento = hexARgbTriplet(acentoHex);
  if (!primario || !acento) return null;

  // Los acentos semánticos son opcionales: si vienen, deben ser HEX válidos.
  let exito = SEMILLA_EXITO_DEFAULT;
  if (exitoHex !== undefined) {
    const t = hexARgbTriplet(exitoHex);
    if (!t) return null;
    exito = t;
  }
  let info = SEMILLA_INFO_DEFAULT;
  if (infoHex !== undefined) {
    const t = hexARgbTriplet(infoHex);
    if (!t) return null;
    info = t;
  }

  // Color de fondo opcional: si viene, debe ser HEX válido. Se guarda como
  // semilla (triplete); las superficies se derivan al inyectar las CSS vars.
  let fondo: string | undefined;
  if (fondoHex !== undefined) {
    const t = hexARgbTriplet(fondoHex);
    if (!t) return null;
    fondo = t;
  }

  const paleta = construirPaleta({ primario, acento, exito, info });
  return fondo ? { ...paleta, fondo } : paleta;
}

/**
 * Normaliza una paleta posiblemente PARCIAL (filas viejas con sólo los 7 tokens
 * originales) a la paleta resuelta completa. Rellena los pasos faltantes
 * derivándolos de `brand500` (marca) y de las semillas `success500`/`info500`
 * (o de los defaults). **Respeta los valores ya presentes** (no re-deriva lo que
 * el usuario afinó). Es el puente de back-compat: ninguna institución existente
 * pierde su branding al introducir los tokens nuevos.
 */
export function normalizarColores(c: BrandingColores): BrandingColoresResuelto {
  const marca = rampaMarca(c.brand500);
  const exito = rampaSemantica(c.success500 ?? SEMILLA_EXITO_DEFAULT);
  const info = rampaSemantica(c.info500 ?? SEMILLA_INFO_DEFAULT);
  return {
    brand50: c.brand50 ?? marca.brand50,
    brand100: c.brand100 ?? marca.brand100,
    brand200: c.brand200 ?? marca.brand200,
    brand300: c.brand300 ?? marca.brand300,
    brand400: c.brand400 ?? marca.brand400,
    brand500: c.brand500,
    brand600: c.brand600 ?? marca.brand600,
    brand700: c.brand700 ?? marca.brand700,
    brand800: c.brand800 ?? marca.brand800,
    brand900: c.brand900 ?? marca.brand900,
    gold500: c.gold500,
    success50: c.success50 ?? exito.s50,
    success400: c.success400 ?? exito.s400,
    success500: c.success500 ?? exito.s500,
    success600: c.success600 ?? exito.s600,
    success700: c.success700 ?? exito.s700,
    info50: c.info50 ?? info.s50,
    info400: c.info400 ?? info.s400,
    info500: c.info500 ?? info.s500,
    info600: c.info600 ?? info.s600,
    info700: c.info700 ?? info.s700,
    // Color de fondo: opcional. Se respeta si está; ausente ⇒ neutros navy.
    ...(c.fondo ? { fondo: c.fondo } : {}),
  };
}
